import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  LocalShipping as TruckIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import * as customerServices from "../../../services/customer";

export default function ShippingDialog({ open, onClose, transaction, onSubmit }) {
  const [shippingName, setShippingName] = useState("");
  const [soNumber, setSoNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Integrated selection states
  const [selectedCarId, setSelectedCarId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [carSelectError, setCarSelectError] = useState(false);
  const mustPickCar = false;

  // Full customer details state
  const [fullCustomer, setFullCustomer] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(false);

  // Use customer from transaction as fallback
  const customer = fullCustomer || transaction?.customer;
  const cars = Array.isArray(customer?.cars) ? customer.cars : [];
  const drivers = Array.isArray(customer?.drivers) ? customer.drivers : [];

  useEffect(() => {
    if (!open) {
      setFullCustomer(null);
      return;
    }

    const fetchFullCustomer = async () => {
      const customerId = transaction?.customer?.id || transaction?.customerId;
      if (!customerId) return;

      try {
        setLoadingCustomer(true);
        const res = await customerServices.getCustomersById(customerId);
        setFullCustomer(res?.data?.data || res?.data);
      } catch (err) {
        console.error("Error fetching full customer in ShippingDialog:", err);
      } finally {
        setLoadingCustomer(false);
      }
    };

    fetchFullCustomer();

    setShippingName(transaction?.shipping?.shippingName || "");
    setSoNumber(transaction?.shipping?.purchaseOrderNumber || "");
    setDriverName(transaction?.shipping?.driverName || "");
    setPhoneNumber(transaction?.shipping?.phoneNumber || transaction?.customer?.phone || "");
    setNote(transaction?.shipping?.note || "");
    setVehicleNumber(transaction?.shipping?.vehicleNumber || "");

    // Car and driver relations belong to the sale transaction, not Shipping.
    setSelectedCarId(transaction?.carId ? String(transaction.carId) : "");
    setSelectedDriverId(transaction?.driverId ? String(transaction.driverId) : "");
    setCarSelectError(false);
  }, [open, transaction]);

  const toggleCar = (id) => {
    const idStr = String(id);
    if (String(selectedCarId) === idStr) {
      setSelectedCarId("");
      setSelectedDriverId(""); // Reset driver if car is removed
      setVehicleNumber("");
    } else {
      setSelectedCarId(idStr);
      setCarSelectError(false);
      setSelectedDriverId(""); // Reset driver when changing car
      const car = cars.find((c) => String(c.id) === idStr);
      setVehicleNumber(car?.license || "");
    }
  };

  const toggleDriver = (id) => {
    const idStr = String(id);
    if (String(selectedDriverId) === idStr) {
      setSelectedDriverId("");
      setDriverName("");
    } else {
      setSelectedDriverId(idStr);
      const drv = drivers.find((d) => String(d.id) === idStr);
      const full = [drv?.name, drv?.surname].filter(Boolean).join(" ").trim();
      setDriverName(full || "");
    }
  };

  const handleSubmit = async () => {
    if (mustPickCar && !selectedCarId) {
      setCarSelectError(true);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        shippingName,
        purchaseOrderNumber: soNumber,
        driverName,
        vehicleNumber,
        phoneNumber,
        note,
        carId: selectedCarId ? Number(selectedCarId) : null,
        driverId: selectedDriverId ? Number(selectedDriverId) : null,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const personTypeLabel =
    customer?.personType === "corporate" ? "นิติบุคคล" : "บุคคลธรรมดา";

  const canSubmit = !submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "24px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <div className="bg-white px-6 py-5 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <span className="w-2 h-8 bg-[#10B981] rounded-full mr-2"></span>
            ข้อมูลการขนส่ง
          </h2>
          {customer && (
            <p className="text-sm text-gray-500 ml-6 mt-1">
              กำลังทำรายการให้กับ: <span className="font-semibold text-gray-700">{customer.name}</span>
            </p>
          )}
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <div className="flex flex-col md:flex-row h-full">
          {/* Left Panel: Customer Info & Selections */}
          <div className="w-full md:w-5/12 bg-gray-50/50 p-6 border-r border-gray-100 overflow-y-auto max-h-[70vh]">
            <div className="space-y-6">

              {/* Customer Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 relative overflow-hidden group hover:border-[#10B981]/30 transition-all">
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#10B981]/10 to-transparent rounded-bl-full -mr-8 -mt-8 opacity-50 group-hover:scale-110 transition-transform"></div>

                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">ลูกค้า</span>
                      <h3 className="text-lg font-bold text-gray-800 mt-1 leading-tight">{customer?.name || "-"}</h3>
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      <span className="px-2 py-1 rounded-lg bg-gray-100 text-gray-600 text-[10px] font-medium border border-gray-200">
                        {personTypeLabel}
                      </span>
                      {customer?.customerType && (
                        <span className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${customer.customerType === "SELLER"
                          ? "bg-orange-50 text-orange-600 border-orange-100"
                          : "bg-blue-50 text-blue-600 border-blue-100"
                          }`}>
                          {customer.customerType === "SELLER" ? "SELLER" : customer.customerType}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3 mt-4">
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-400">เบอร์โทรศัพท์</p>
                        <p className="font-medium text-gray-700">{customer?.phone || "-"}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-400">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-400">ที่อยู่</p>
                        <p className="font-medium text-gray-700 leading-relaxed">{customer?.address || "-"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cars Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                    <TruckIcon sx={{ fontSize: 18 }} className="text-gray-400" />
                    รถที่ลงทะเบียนไว้
                  </h4>
                  <div className="flex items-center gap-2">
                    {loadingCustomer && <CircularProgress size={12} thickness={6} />}
                    <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                      {cars.length ? "เลือกหรือไม่ก็ได้" : "ไม่มีข้อมูล"}
                    </span>
                  </div>
                </div>

                {cars.length > 0 ? (
                  <div className="grid gap-2 max-h-[200px] overflow-y-auto pr-1 custom-scrollbar">
                    {cars.map((car) => {
                      const isSelected = String(selectedCarId) === String(car.id);
                      return (
                        <div
                          key={car.id}
                          onClick={() => toggleCar(car.id)}
                          className={`
                             relative p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3
                             ${isSelected
                              ? "border-[#10B981] bg-[#10B981]/5 shadow-md"
                              : "border-transparent bg-white shadow-sm hover:border-[#10B981]/30 hover:shadow-md"
                            }
                           `}
                        >
                          <div className={`
                              w-10 h-10 rounded-lg flex items-center justify-center transition-colors
                              ${isSelected ? "bg-[#10B981] text-white" : "bg-gray-100 text-gray-400"}
                           `}>
                            <TruckIcon fontSize="small" />
                          </div>
                          <div className="flex-1">
                            <p className={`font-bold text-sm ${isSelected ? "text-gray-800" : "text-gray-600"}`}>
                              {car.license}
                            </p>
                            <p className="text-xs text-gray-400">
                              {car.province} {car.type && `• ${car.type}`}
                            </p>
                          </div>
                          {isSelected && (
                            <div className="animate-in zoom-in duration-200">
                              <CheckCircleIcon className="text-[#10B981]" fontSize="small" />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center">
                    <TruckIcon className="text-gray-300 mb-1" />
                    <p className="text-xs text-gray-400">ไม่มีรถที่ลงทะเบียนไว้</p>
                  </div>
                )}
                {carSelectError && <p className="text-xs text-red-500 mt-1 font-medium">* กรุณาเลือกรถ 1 คัน</p>}
              </div>

              {/* Drivers Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                    <BadgeIcon sx={{ fontSize: 18 }} className="text-gray-400" />
                    คนขับที่ลงทะเบียนไว้
                  </h4>
                  <span className="text-[10px] bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {drivers.length ? "เลือกหรือไม่ก็ได้" : "ไม่มีข้อมูล"}
                  </span>
                </div>

                <div
                  className={`
                    relative rounded-xl transition-all
                    ${!selectedCarId && drivers.length > 0 ? "opacity-60 grayscale pointer-events-none" : ""}
                  `}
                >
                  {!selectedCarId && drivers.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <span className="bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm font-medium shadow-lg">
                        กรุณาเลือกรถก่อน
                      </span>
                    </div>
                  )}

                  {drivers.length > 0 ? (
                    <div className="grid gap-2 max-h-[160px] overflow-y-auto pr-1">
                      {drivers.map((drv) => {
                        const isSelected = String(selectedDriverId) === String(drv.id);
                        return (
                          <div
                            key={drv.id}
                            onClick={() => toggleDriver(drv.id)}
                            className={`
                                p-3 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-3
                                ${isSelected
                                ? "border-orange-500 bg-orange-50 shadow-md"
                                : "border-transparent bg-white shadow-sm hover:border-orange-300 hover:shadow-md"
                              }
                              `}
                          >
                            <div className={`
                                  w-10 h-10 rounded-lg flex items-center justify-center transition-colors
                                  ${isSelected ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-400"}
                               `}>
                              <BadgeIcon fontSize="small" />
                            </div>
                            <div className="flex-1">
                              <p className={`font-bold text-sm ${isSelected ? "text-gray-800" : "text-gray-600"}`}>
                                {[drv?.name, drv?.surname].filter(Boolean).join(" ") || "-"}
                              </p>
                              {drv?.idCard && <p className="text-xs text-gray-400">ID: {drv.idCard}</p>}
                            </div>
                            {isSelected && (
                              <div className="animate-in zoom-in duration-200">
                                <CheckCircleIcon className="text-orange-500" fontSize="small" />
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center text-center">
                      <BadgeIcon className="text-gray-300 mb-1" />
                      <p className="text-xs text-gray-400">ไม่มีคนขับที่ลงทะเบียนไว้</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Right Panel: Form Inputs */}
          <div className="w-full md:w-7/12 p-6 md:p-8 flex flex-col h-full overflow-y-auto">
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800">กรอกข้อมูลการขนส่ง</h3>
              <p className="text-sm text-gray-500">ระบุรายละเอียดเพิ่มเติมสำหรับการขนส่งครั้งนี้</p>
            </div>

            <Stack spacing={3} className="flex-1">
              <TextField
                label="ชื่อการขนส่ง"
                value={shippingName}
                onChange={(e) => setShippingName(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{ sx: { borderRadius: 3 } }}
              />
              <TextField
                label="เลขที่ใบสั่งซื้อ (PO)"
                value={soNumber}
                onChange={(e) => setSoNumber(e.target.value)}
                fullWidth
                InputProps={{ sx: { borderRadius: 3 } }}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <TextField
                  label="ชื่อคนขับ"
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  fullWidth
                  InputProps={{ sx: { borderRadius: 3 } }}
                  helperText={selectedDriverId ? "ดึงจากระบบอัตโนมัติ" : ""}
                />
                <TextField
                  label="เลขตู้ / ทะเบียน"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  fullWidth
                  InputProps={{ sx: { borderRadius: 3 } }}
                  helperText={selectedCarId ? "ดึงจากระบบอัตโนมัติ" : ""}
                />
              </div>

              <TextField
                label="โทรศัพท์"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
                InputProps={{ sx: { borderRadius: 3 } }}
              />

              <TextField
                label="หมายเหตุ"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={4}
                placeholder="ระบุรายละเอียดอื่นๆ เพิ่มเติม..."
                InputProps={{ sx: { borderRadius: 3 } }}
                helperText="* ข้อมูลนี้จะแสดงในใบรายการสินค้า"
              />
            </Stack>
          </div>
        </div>
      </DialogContent>

      <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 z-10">
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{
            borderRadius: 3,
            px: 3,
            py: 1.2,
            color: 'text.secondary',
            fontSize: '1rem'
          }}
        >
          ยกเลิก
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          disableElevation
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.2,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
            }
          }}
        >
          {submitting ? (
            <div className="flex items-center gap-2">
              <CircularProgress size={20} color="inherit" />
              <span>กำลังบันทึก...</span>
            </div>
          ) : (
            "บันทึก"
          )}
        </Button>
      </div>
    </Dialog >
  );
}
